import { Download, FileCheck, Lock, LockOpen } from 'lucide-react';
import * as React from 'react';
import { atLeast, formatDuration } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Skeleton,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { currentMonthFilter, FilterBar } from '@/components/reporting/filter-bar';
import { PageContainer } from '@/components/shell/page-container';
import { SavedFilters } from '@/components/reporting/saved-filters';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError } from '@/lib/api';
import {
  exportCsvUrl, useExportSummary, useMarkExported, useUnmarkExported, type Filters,
} from '@/lib/reporting-api';
import type { Session } from '@/lib/session';

/**
 * Hand recorded hours to whatever consumes them next - payroll, a client, an
 * invoice - and then close them.
 *
 * The two halves are deliberately separate actions in a deliberate order. You
 * download first and mark second, because marking is the irreversible half: a
 * marked entry becomes read-only, so the figure that left the building and the
 * figure still in here cannot drift apart afterwards. Doing it the other way
 * round would lock hours before anybody had checked the file.
 *
 * The mark is driven by the same FILTER as the download rather than a list of
 * rows the browser collected, so the two provably describe the same entries.
 */
export function ExportPage({ session }: { session: Session }) {
  const [filters, setFilters] = React.useState<Filters>(currentMonthFilter);
  const [downloaded, setDownloaded] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { data, isPending } = useExportSummary(filters);
  const mark = useMarkExported();
  const unmark = useUnmarkExported();

  const changeFilters = (next: Filters) => {
    setFilters(next);
    // A download of the previous filter says nothing about this one.
    setDownloaded(false);
    setNotice(null);
  };

  const runMark = async () => {
    setError(null);
    setNotice(null);
    try {
      const { marked } = await mark.mutateAsync(filters);
      setNotice(
        marked === 0
          ? 'Nothing changed — everything matching was already marked.'
          : `${marked} ${marked === 1 ? 'entry is' : 'entries are'} now marked as exported and read-only.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not mark those entries.');
    }
  };

  const runUnmark = async () => {
    setError(null);
    setNotice(null);
    try {
      const { cleared, onInvoice } = await unmark.mutateAsync(filters);
      setNotice(
        `${cleared} ${cleared === 1 ? 'entry is' : 'entries are'} editable again.` +
          (onInvoice > 0
            ? ` ${onInvoice} on an invoice kept their mark: cancel the invoice to release those.`
            : ''),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not clear the mark.');
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Export"
        description="Take hours out for payroll or invoicing, then close them so they cannot change."
      />

      <FilterBar value={filters} onChange={changeFilters} role={session.role}>
        <SavedFilters screen="export" value={filters} onChange={changeFilters} />
      </FilterBar>

      {isPending && <Skeleton className="h-40 w-full" />}

      {data && (
        <Card>
          <CardContent className="space-y-5 py-5">
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              <Figure label="Entries" value={data.entries.toLocaleString('en-IN')} />
              <Figure label="Total" value={formatDuration(data.totalSeconds)} />
              <Figure
                label="Not yet exported"
                value={data.notYetExported.toLocaleString('en-IN')}
                tone={data.notYetExported > 0 ? 'text-primary' : undefined}
              />
              {data.alreadyExported > 0 && (
                <Figure
                  label="Already exported"
                  value={data.alreadyExported.toLocaleString('en-IN')}
                />
              )}
            </div>

            {data.entries === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing matches that filter.</p>
            ) : (
              <>
                {/* Step one. Always available - re-downloading a file is harmless,
                    and somebody will want the same file twice. */}
                <div className="flex flex-wrap items-center gap-3">
                  <Button hint={"Download the file"} asChild onClick={() => setDownloaded(true)}>
                    <a href={exportCsvUrl(filters)}>
                      <Download className="size-4" />
                      Download {data.entries.toLocaleString('en-IN')}{' '}
                      {data.entries === 1 ? 'entry' : 'entries'}
                    </a>
                  </Button>
                  {downloaded && (
                    <Badge variant="secondary">
                      <FileCheck className="size-3" />
                      Downloaded
                    </Badge>
                  )}
                </div>

                {/* Step two, and only offered once the file has been taken: marking
                    is the half that cannot be undone from this screen. */}
                <div className="space-y-2 border-t pt-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <ConfirmButton
                      variant="outline"
                      disabled={!downloaded || data.notYetExported === 0 || mark.isPending}
                      pending={mark.isPending}
                      title={`Mark ${data.notYetExported.toLocaleString('en-IN')} entries as exported?`}
                      description={
                        <>
                          They become read-only: nobody can edit or delete them afterwards,
                          including the people who recorded them. That is the point — the
                          hours you have just taken out and the hours still in here must not
                          drift apart. An administrator can clear the mark again here.
                        </>
                      }
                      confirmLabel="Mark as exported"
                      destructive
                      onConfirm={() => void runMark()}
                    >
                      <Lock className="size-4" />
                      Mark {data.notYetExported.toLocaleString('en-IN')} as exported
                    </ConfirmButton>

                    {!downloaded && (
                      <span className="text-xs text-muted-foreground">
                        Download the file first — marking locks these entries.
                      </span>
                    )}
                    {downloaded && data.notYetExported === 0 && (
                      <span className="text-xs text-muted-foreground">
                        Everything matching is already marked.
                      </span>
                    )}

                    {atLeast(session.role, 'ADMIN') && data.alreadyExported > 0 && (
                      <ConfirmButton
                        variant="ghost"
                        disabled={unmark.isPending}
                        pending={unmark.isPending}
                        title={`Clear the exported mark on ${data.alreadyExported.toLocaleString('en-IN')} entries?`}
                        description={
                          <>
                            They become editable again, so the hours here can drift from the
                            file already handed over. Do this to correct a mark put on the
                            wrong range. Entries on an invoice keep their mark until the
                            invoice is cancelled.
                          </>
                        }
                        confirmLabel="Clear the mark"
                        onConfirm={() => void runUnmark()}
                      >
                        <LockOpen className="size-4" />
                        Clear the exported mark
                      </ConfirmButton>
                    )}
                  </div>
                </div>
              </>
            )}

            {notice && (
              <Alert>
                <AlertDescription className="text-sm">{notice}</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert className="border-destructive/30">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`ts-num text-xl font-semibold ${tone ?? ''}`}>{value}</div>
    </div>
  );
}
